<template>
  <div class="inline-flex">
    <slot v-if="!withoutButton" :show="show" :set-show="setShow">
      <BaseButton small primary @click="setShow(true)"> New Offer </BaseButton>
    </slot>
    <Portal to="app-dialog" v-if="show">
      <VModal ref="modal" @modal:hidden="handleHide" disable-backdrop-click>
        <div class="w-full max-w-6xl mx-auto">
          <div class="mb-4">
            <div class="flex items-center justify-end">
              <div class="flex-none" @click.stop>
                <button
                  class="h-10 w-10 inline-flex items-center justify-center bg-white hover:bg-gray-200 focus:outline-none rounded-full"
                  @click="$refs.modal.close()"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-5 h-5">
                    <path
                      d="M12 10.586l4.95-4.95 1.414 1.414-4.95
                      4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95
                      4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05
                      5.636z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          <div class="rounded shadow-xs w-full max-w-6xl mx-auto" @click.stop>
            <div class="rounded shadow-lg bg-white overflow-hidden divide-y divide-gray-200">
              <div class="bg-white px-4 sm:px-6 py-5">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <h1 class="text-sm">New Offer</h1>
                  </div>
                </div>
              </div>
              <template>
                <div class="bg-gray-200">
                  <BaseVeeForm :action="submitForm" v-slot="{ isPending, invalid }">
                    <TabbedNavigation>
                      <template #general>
                        <General
                          :issue-date="formData.issueDate"
                          :number="formData.number"
                          :assigned-user-id="formData.assignedUserId"
                          :account-id="accountId"
                          can-edit
                          @update:issue-date="formData.issueDate = $event"
                          @update:assigned-user-id="formData.assignedUserId = $event"
                        />
                      </template>

                      <template #issuer>
                        <Issuer
                          :account-id="accountId"
                          :issuer-name="formData.issuerName"
                          :issuer-address="formData.issuerAddress"
                          :issuer-vat-number="formData.issuerVatNumber"
                          :issuer-vat-office="formData.issuerVatOffice"
                          :can-change-account="canChangeIssuer"
                          :can-update-account="canUpdateIssuer"
                          @update:issuer-name="formData.issuerName = $event"
                          @update:issuer-address="formData.issuerAddress = $event"
                          @update:issuer-vat-number="formData.issuerVatNumber = $event"
                          @update:issuer-vat-office="formData.issuerVatOffice = $event"
                        />
                      </template>

                      <template #recipient>
                        <Recipient
                          :contact-id="formData.contactId"
                          :contact-request-id="formData.contactRequestId"
                          :account-relation-id="formData.accountRelationUuid"
                          :recipient-name="formData.recipientName"
                          :recipient-address="formData.recipientAddress"
                          :recipient-vat-number="formData.recipientVatNumber"
                          :recipient-vat-office="formData.recipientVatOffice"
                          :can-change-recipient="canChangeRecipient"
                          :can-update-recipient="canUpdateRecipient"
                          @update:recipient-name="formData.recipientName = $event"
                          @update:recipient-address="formData.recipientAddress = $event"
                          @update:recipient-vat-number="formData.recipientVatNumber = $event"
                          @update:recipient-vat-office="formData.recipientVatOffice = $event"
                        />
                      </template>

                      <template #details>
                        <Lines
                          :can-edit-lines="canEditLines"
                          :offer-lines="formData.offerLines"
                          :default-vat-rate="defaultVatRate"
                          @change="updateOfferLines"
                        />
                      </template>

                      <template #preview>
                        <div class="flex flex-col items-center space-y-4">
                          <div
                            class="bg-white rounded-lg border border-gray-400 overflow-hidden pb-16"
                          >
                            <PrintableOffer :offer="formData" :account-relation="accountRelation" />
                          </div>
                        </div>
                      </template>
                    </TabbedNavigation>
                    <div class="space-y-6 p-4 sm:p-6 bg-white border-t">
                      <div class="flex space-x-2 sticky bottom-0">
                        <BaseButton
                          primary
                          :disabled="isPending || invalid || formData.offerLines.length === 0"
                        >
                          Create offer
                        </BaseButton>
                      </div>
                    </div>
                  </BaseVeeForm>
                </div>
              </template>
            </div>
          </div>
        </div>
      </VModal>
    </Portal>
  </div>
</template>

<script>
import moment from 'moment';
import PrintableOffer from 'Components/Offers/PrintableOffer.vue';
import TabbedNavigation from './TabbedNavigation.vue';
import General from './General.vue';
import Issuer from './Issuer.vue';
import Recipient from './Recipient.vue';
import Lines from './Lines.vue';

export default {
  components: {
    TabbedNavigation,
    General,
    Issuer,
    Recipient,
    Lines,
    PrintableOffer,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
    defaultFormData: {
      type: Object,
      default: () => ({}),
    },
    canChangeIssuer: Boolean,
    canUpdateIssuer: Boolean,
    canChangeRecipient: Boolean,
    canUpdateRecipient: Boolean,
    canEditLines: Boolean,
    withoutButton: Boolean,
  },
  data() {
    return {
      show: false,
      formData: {},
    };
  },
  computed: {
    accountRelation() {
      return (
        this.$store.state.entities.accountRelations.byId[this.formData.accountRelationUuid] || null
      );
    },
    defaultVatRate() {
      return this.accountRelation?.defaultVatRate || 0;
    },
  },
  watch: {
    show(value) {
      if (!value) {
        this.setFormData();
      }
    },
  },
  created() {
    this.setFormData();
  },
  methods: {
    handleHide() {
      this.setShow(false);
      this.$emit('hide');
    },
    setShow(value) {
      this.show = value;
    },
    setFormData() {
      this.formData = {
        number: '',
        issueDate: moment().format('YYYY-MM-DD'),
        issuerName: '',
        issuerAddress: '',
        issuerVatNumber: '',
        issuerVatOffice: '',
        recipientName: '',
        recipientAddress: '',
        recipientVatNumber: '',
        recipientVatOffice: '',
        contactId: null,
        assignedUserId: null,
        accountRelationUuid: null,
        offerLines: [],
        ...this.defaultFormData,
      };
    },
    updateOfferLines(offerLines) {
      this.formData.offerLines = offerLines;
    },
    async submitForm() {
      try {
        await this.$store.dispatch({
          type: 'ui/accountOffers/store',
          accountUuid: this.accountId,
          formData: this.formData,
        });

        this.$emit('success');

        this.setShow(false);

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
